import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { forkJoin } from 'rxjs';
import { Product } from '../../../models/Product';
import { Variety, VarietySaveRequest, VarietyStatus } from '../../../models/Variety';
import { CatalogoService } from '../../../service/catalogo.service';
import { GeneralService } from '../../../service/general.service';
import { ProductService } from '../../../service/product.service';
import { VarietyService } from '../../../service/variety.service';
import { getCurrentUserId } from '../../../shared/utils/auth-user.util';
import { TransactionAccessService } from '../../../shared/services/transaction-access.service';

@Component({
  selector: 'app-modal-edit-variety',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgSelectModule],
  templateUrl: './modal-edit-variety.html',
  styleUrls: ['./modal-edit-variety.scss']
})
export class ModalEditVariety implements OnChanges, OnInit {
  @Input() variety: Variety | null = null;
  @Output() deleted = new EventEmitter<number>();
  @Output() updated = new EventEmitter<Variety>();

  private readonly fb = inject(FormBuilder);
  private readonly varietyService = inject(VarietyService);
  private readonly productService = inject(ProductService);
  private readonly catalogoService = inject(CatalogoService);
  private readonly generalService = inject(GeneralService);
  private readonly transactionAccess = inject(TransactionAccessService);

  readonly products = signal<Product[]>([]);
  readonly measurementUnits = signal<any[]>([]);
  readonly sectionName = signal('');
  readonly divisionName = signal('');
  readonly className = signal('');
  readonly message = signal('');
  readonly error = signal(false);
  readonly submitting = signal(false);
  readonly statuses = [
    { value: 'A' as VarietyStatus, label: 'Activo' },
    { value: 'I' as VarietyStatus, label: 'Inactivo' }
  ];

  readonly form = this.fb.group({
    sectionId: this.fb.control<number | null>(null, Validators.required),
    divisionId: this.fb.control<number | null>(null, Validators.required),
    classId: this.fb.control<number | null>(null, Validators.required),
    productId: this.fb.control<number | null>(null, Validators.required),
    ineCode: ['', Validators.required],
    name: ['', Validators.required],
    measurementUnitId: this.fb.control<number | null>(null, Validators.required),
    baseQuantity: this.fb.control<number | null>(null, [Validators.required, Validators.min(0.0000001)]),
    ciiuCode: [''],
    naegCode: [''],
    npgCode: [''],
    ccpCode: [''],
    reference: [''],
    status: this.fb.control<VarietyStatus | null>('A', Validators.required)
  });

  ngOnInit(): void {
    forkJoin({
      products: this.productService.getAll(),
      units: this.generalService.get('GET_UNIDADES_MEDIDA')
    }).subscribe({
      next: ({ products, units }) => {
        this.products.set(products ?? []);
        this.measurementUnits.set(units ?? []);
        this.loadSelectedClassification();
      },
      error: () => this.setMessage('No fue posible cargar los catálogos.', true)
    });
  }

  ngOnChanges(): void {
    if (!this.variety) return;
    this.form.reset({
      sectionId: this.variety.sectionId,
      divisionId: this.variety.divisionId,
      classId: this.variety.classId,
      productId: this.variety.productId,
      ineCode: this.variety.ineCode,
      name: this.variety.name,
      measurementUnitId: this.variety.measurementUnitId,
      baseQuantity: this.variety.baseQuantity,
      ciiuCode: this.variety.ciiuCode,
      naegCode: this.variety.naegCode,
      npgCode: this.variety.npgCode,
      ccpCode: this.variety.ccpCode,
      reference: this.variety.reference,
      status: this.variety.status
    });
    this.message.set('');
    this.loadSelectedClassification();
  }

  onProductChange(product: Product | null): void {
    this.clearClassification();
    if (!product?.codigo) {
      this.form.patchValue({ sectionId: null, divisionId: null, classId: null, productId: null });
      return;
    }
    this.form.patchValue({
      sectionId: product.fkSeccion,
      divisionId: product.fkDivision,
      classId: product.fkClase,
      productId: product.codigo
    });
    this.loadClassification(product);
  }

  clearProduct(): void {
    this.onProductChange(null);
  }

  saveChanges(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.setMessage(
        'Complete los campos obligatorios: producto, código INE, nombre, unidad de medida, cantidad base y estado.',
        true
      );
      return;
    }
    if (!this.variety?.id || this.submitting()) return;
    const transactionId = this.getTransactionId();
    if (!transactionId) return;
    const request = this.buildRequest(transactionId);
    this.submitting.set(true);
    this.message.set('');
    this.varietyService.edit(this.variety.id, request).subscribe({
      next: message => {
        this.submitting.set(false);
        const updated: Variety = { id: this.variety!.id, ...request };
        this.setMessage(message || 'Variedad actualizada correctamente.', false);
        this.updated.emit(updated);
      },
      error: (message: string) => {
        this.submitting.set(false);
        this.setMessage(message, true);
      }
    });
  }

  confirmDelete(): void {
    if (!this.variety?.id || this.submitting()) return;
    const transactionId = this.getTransactionId();
    if (!transactionId) return;
    this.submitting.set(true);
    this.varietyService.delete(
      this.variety.id,
      getCurrentUserId(),
      transactionId
    ).subscribe({
      next: message => {
        this.submitting.set(false);
        this.setMessage(message || 'Variedad eliminada correctamente.', false);
        this.deleted.emit(this.variety!.id!);
        this.closeDeleteModal();
        this.closeModal();
      },
      error: (message: string) => {
        this.submitting.set(false);
        this.setMessage(message, true);
      }
    });
  }

  openDeleteModal(): void {
    if (this.submitting()) return;
    new (window as any).bootstrap.Modal(document.getElementById('modalDeleteVariety')).show();
  }

  private buildRequest(transactionId: number): VarietySaveRequest {
    const value = this.form.getRawValue();
    return {
      sectionId: value.sectionId!,
      divisionId: value.divisionId!,
      classId: value.classId!,
      productId: value.productId!,
      ineCode: value.ineCode?.trim() ?? '',
      name: value.name?.trim() ?? '',
      measurementUnitId: value.measurementUnitId!,
      baseQuantity: value.baseQuantity!,
      ciiuCode: value.ciiuCode?.trim() ?? '',
      naegCode: value.naegCode?.trim() ?? '',
      npgCode: value.npgCode?.trim() ?? '',
      ccpCode: value.ccpCode?.trim() ?? '',
      reference: value.reference?.trim() ?? '',
      status: value.status!,
      auditUserId: getCurrentUserId(),
      auditTransactionId: transactionId
    };
  }

  private loadSelectedClassification(): void {
    const product = this.products().find(item => item.codigo === this.form.getRawValue().productId);
    if (product) this.loadClassification(product);
  }

  private loadClassification(product: Product): void {
    forkJoin({
      sections: this.catalogoService.getSecciones(),
      divisions: this.catalogoService.getDivisiones(product.fkSeccion),
      classes: this.catalogoService.getClases(product.fkDivision)
    }).subscribe({
      next: ({ sections, divisions, classes }) => {
        this.sectionName.set(sections.find(x => x.codigo === product.fkSeccion)?.nombre ?? '');
        this.divisionName.set(divisions.find(x => x.codigo === product.fkDivision)?.nombre ?? '');
        this.className.set(classes.find(x => x.codigo === product.fkClase)?.nombre ?? '');
      },
      error: () => this.setMessage('No fue posible cargar la clasificación del producto.', true)
    });
  }

  private clearClassification(): void {
    this.sectionName.set('');
    this.divisionName.set('');
    this.className.set('');
  }

  private setMessage(message: string, isError: boolean): void {
    this.message.set(message);
    this.error.set(isError);
  }

  private getTransactionId(): number | null {
    const transactionId = this.transactionAccess.getTransactionId('varieties');
    if (!transactionId) {
      this.setMessage(
        'No se encontró la transacción de mantenimiento de variedades asignada al usuario.',
        true
      );
    }
    return transactionId;
  }

  private closeDeleteModal(): void {
    (window as any).bootstrap.Modal
      .getInstance(document.getElementById('modalDeleteVariety'))?.hide();
  }

  private closeModal(): void {
    (window as any).bootstrap.Modal
      .getInstance(document.getElementById('modalEditVariety'))?.hide();
  }
}
